<template>
  <div class="chat-app flex flex-col flex-1 min-h-0">
    <!-- 質問回数ヘッダー（埋め込み時はドロワー見出しと重複しないようタイトル非表示） -->
    <div
      class="flex items-center px-5 py-2.5 border-b border-gray-100 flex-shrink-0 bg-white/80"
      :class="embedMode ? 'justify-end' : 'justify-between'"
    >
      <div v-if="!embedMode" class="text-[13px] font-medium text-gray-600">ノウハウ回答 AI</div>
      <div class="flex items-center gap-1.5 text-xs flex-shrink-0">
        <span class="text-gray-400">今月の質問回数：</span>
        <span :class="['font-semibold', localIsReached ? 'text-red-500' : 'text-[#37acdb]']">
          {{ localCount }} / {{ isUnlimited ? '無制限' : limitCount }}
        </span>
        <span v-if="isUnlimited" class="text-green-600 font-medium ml-0.5">（無制限）</span>
        <a
          v-else-if="localIsReached"
          :href="planChangeUrl"
          class="ml-1 text-[#37acdb] underline hover:no-underline"
        >プラン変更</a>
      </div>
    </div>

    <!-- メッセージエリア -->
    <div
      ref="messagesRef"
      class="flex-1 overflow-y-auto p-4 md:p-6 space-y-1 relative pb-36 md:pb-0"
      style="line-height: 1.75"
    >
      <!-- AI初期メッセージ（枠なし・ChatGPT風） -->
      <div class="flex items-start gap-3 mb-6">
        <div class="w-8 h-8 rounded-full bg-white flex-shrink-0 overflow-hidden shadow-sm border border-gray-100 mt-0.5">
          <img :src="aiIconUrl" alt="AI" class="w-full h-full object-cover" />
        </div>
        <div class="flex-1 max-w-[720px] pt-1">
          <p class="text-[15px] text-gray-700 leading-[1.8]">
            こんにちは！Instagramマーケティングについて、どのようなことでお困りですか？
          </p>
        </div>
      </div>

      <!-- メッセージ一覧 -->
      <ChatMessage
        v-for="(msg, idx) in messages"
        :key="msg.id || idx"
        :message="msg"
        :user-avatar-url="userAvatarUrl"
        :ai-icon-url="aiIconUrl"
      />

      <!-- ローディングスケルトン -->
      <ChatSkeleton v-if="isLoading" :ai-icon-url="aiIconUrl" />
    </div>

    <!-- 入力エリア（sticky bottom） -->
    <ChatInput
      v-model="inputValue"
      placeholder=""
      :is-loading="isLoading"
      :is-limit-reached="localIsReached"
      :is-unlimited="isUnlimited"
      :is-demo-mode="isDemoMode"
      :plan-change-url="planChangeUrl"
      @submit="onSubmit"
      @stop="onStop"
    />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import ChatMessage from './components/ChatMessage.vue';
import ChatInput from './components/ChatInput.vue';
import ChatSkeleton from './components/ChatSkeleton.vue';
import { parseMarkdown, stripEmoji } from './utils/markdown';

const props = defineProps({
  initialMessages: { type: Array, default: () => [] },
  initialMessage: { type: String, default: '' },
  sessionId: { type: String, default: '' },
  streamUrl: { type: String, default: '' },
  csrfToken: { type: String, default: '' },
  userAvatarUrl: { type: String, default: null },
  aiIconUrl: { type: String, default: '/img/common/ai_img.svg' },
  currentCount: { type: Number, default: 0 },
  limitCount: { type: [Number, null], default: null },
  isReachedLimit: { type: Boolean, default: false },
  isUnlimited: { type: Boolean, default: false },
  planChangeUrl: { type: String, default: '#' },
  onUpdateCurrentCount: { type: Function, default: null },
  isDemoMode: { type: Boolean, default: false },
  /** true のとき URL の ?message を消さない（ダッシュボード埋め込み用） */
  embedMode: { type: Boolean, default: false },
});

const emit = defineEmits(['update:currentCount']);

const messagesRef = ref(null);
const inputValue = ref('');
const messages = ref([]);
const isLoading = ref(false);
let streamController = null;
let messageIdCounter = 0;

const localCount = ref(props.currentCount);
const localIsReached = computed(() => {
  if (props.isUnlimited || props.limitCount === null) return false;
  return localCount.value >= Number(props.limitCount);
});


// 初期メッセージを変換
function normalizeMessage(conv) {
  const content = conv.is_user_message ? conv.message : conv.response;
  return {
    id: `msg-${++messageIdCounter}`,
    content,
    isUser: !!conv.is_user_message,
    timestamp: conv.created_at || new Date(),
    isStreaming: false,
    parsedHtml: conv.is_user_message ? null : parseMarkdown(stripEmoji(content || '')),
  };
}

onMounted(() => {
  messages.value = (props.initialMessages || []).map(normalizeMessage);

  if (props.isDemoMode) return;

  // ?message= パラメータがあれば自動送信（ノウハウAI単体ページでは URL から除去して再送防止）
  if (props.initialMessage && props.initialMessage.trim()) {
    if (!props.embedMode) {
      const url = new URL(window.location.href);
      url.searchParams.delete('message');
      history.replaceState(null, '', url.toString());
    }
    nextTick(() => {
      setTimeout(() => onSubmit(props.initialMessage.trim()), 300);
    });
  }
});

function scrollToBottom() {
  const el = messagesRef.value;
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
}

async function onSubmit(text) {
  if (props.isDemoMode || !text.trim() || isLoading.value || localIsReached.value) return;

  const userMsg = {
    id: `msg-${++messageIdCounter}`,
    content: text,
    isUser: true,
    timestamp: new Date(),
    isStreaming: false,
  };
  messages.value.push(userMsg);
  inputValue.value = '';
  isLoading.value = true;

  // AIメッセージはまだ追加しない（最初のチャンク到着時に追加してスケルトンと重複させない）
  let aiMsgId = null;

  nextTick(() => scrollToBottom());

  streamController = new AbortController();
  const formData = new FormData();
  formData.append('message', text);
  formData.append('session_id', props.sessionId);
  formData.append('_token', props.csrfToken);

  let fullText = '';
  let isResponseStarted = false;

  try {
    const res = await fetch(props.streamUrl, {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'text/event-stream', 'X-Requested-With': 'XMLHttpRequest' },
      signal: streamController.signal,
    });

    if (!res.ok) {
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '質問回数の上限に達しています。');
      }
      throw new Error('Network response was not ok');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'chunk' && data.content) {
            const stripped = stripEmoji(data.content);
            if (stripped) {
              fullText += stripped;
              if (!isResponseStarted) {
                isResponseStarted = true;
                // 最初のチャンク到着時にAIメッセージを追加しスケルトンを非表示にする
                aiMsgId = `msg-${++messageIdCounter}`;
                messages.value.push({
                  id: aiMsgId,
                  content: fullText,
                  isUser: false,
                  timestamp: new Date(),
                  isStreaming: true,
                  parsedHtml: null,
                });
                isLoading.value = false;
                const newCount = localCount.value + 1;
                localCount.value = newCount;
                emit('update:currentCount', newCount);
                props.onUpdateCurrentCount?.(newCount);
              } else {
                const last = messages.value[messages.value.length - 1];
                if (last && !last.isUser) {
                  last.content = fullText;
                  last.parsedHtml = null;
                  last.isStreaming = true;
                }
              }
              nextTick(() => {
                if (messagesRef.value) {
                  const nearBottom = messagesRef.value.scrollTop + messagesRef.value.clientHeight >= messagesRef.value.scrollHeight - 150;
                  if (nearBottom) scrollToBottom();
                }
              });
            }
          } else if (data.type === 'end') {
            // 完了
          }
        } catch (e) {
          console.error('Parse error:', e);
        }
      }
    }

    // ストリーミング完了
    const last = messages.value[messages.value.length - 1];
    if (last && !last.isUser) {
      last.content = fullText;
      last.parsedHtml = parseMarkdown(stripEmoji(fullText));
      last.isStreaming = false;
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    if (isResponseStarted) {
      // すでにAIメッセージが追加されている場合はその内容をエラーにする
      const last = messages.value[messages.value.length - 1];
      if (last && !last.isUser) {
        last.content = err.message || 'エラーが発生しました。しばらく時間をおいて再試行してください。';
        last.parsedHtml = null;
        last.isStreaming = false;
      }
      const reverted = localCount.value - 1;
      localCount.value = reverted;
      emit('update:currentCount', reverted);
      props.onUpdateCurrentCount?.(reverted);
    } else {
      // まだAIメッセージが追加されていない場合はエラーメッセージを追加する
      messages.value.push({
        id: `msg-${++messageIdCounter}`,
        content: err.message || 'エラーが発生しました。しばらく時間をおいて再試行してください。',
        isUser: false,
        timestamp: new Date(),
        isStreaming: false,
        parsedHtml: null,
      });
    }
  } finally {
    isLoading.value = false;
    streamController = null;
    nextTick(() => scrollToBottom());
  }
}

function onStop() {
  if (streamController) {
    streamController.abort();
  }
}

</script>
